import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowDown, Diamond } from "lucide-react";
import { useMenu } from "@/menu/MenuApp";
import { loc, sget, phil } from "@/menu/i18n";
import { Link } from "react-router-dom";

const fadeUp = { hidden: { opacity: 0, y: 36 }, visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] } }) };

const MenuHome = () => {
  const { data, lang, t } = useMenu();
  const s = data.settings;

  return (
    <div data-testid="menu-home">
      <section className="relative flex min-h-[88vh] items-center justify-center overflow-hidden" data-testid="menu-hero">
        <img src={s.hero_image} alt={s.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#0a0a0b]" />
        <div className="relative z-10 px-6 text-center">
          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={0.1} className="mb-5 text-[11px] font-medium uppercase tracking-[0.4em] text-[#C19D60]">
            {sget(s, "claim", lang)}
          </motion.p>
          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={0.25} data-testid="menu-hero-title" className="font-display text-5xl font-semibold tracking-tight sm:text-7xl">
            {s.name}
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={0.4} className="mx-auto mt-6 max-w-md text-sm font-light leading-relaxed text-[#d8d8d8] sm:text-base">
            {sget(s, "hero_sub", lang)}
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.55}>
            <button
              data-testid="menu-discover-btn"
              onClick={() => document.getElementById("esplora")?.scrollIntoView({ behavior: "smooth" })}
              className="group relative mt-10 overflow-hidden rounded-[5px] bg-[#C19D60] px-10 py-4 text-[12px] font-semibold uppercase tracking-[0.25em] text-[#0a0a0b]"
            >
              <span className="absolute inset-0 origin-left scale-x-0 bg-[#E5CAA4] transition-transform duration-500 group-hover:scale-x-100" />
              <span className="relative">{t("discover")}</span>
            </button>
          </motion.div>
        </div>
        <div className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-center">
          <p className="mb-2 text-[9px] uppercase tracking-[0.35em] text-[#A3A3A3]">{t("scroll")}</p>
          <ArrowDown size={14} className="mx-auto animate-bounce text-[#C19D60]" />
        </div>
      </section>

      <div data-testid="menu-marquee" className="border-y border-[#C19D60]/10 bg-[#080809] py-4">
        <Marquee speed={30} gradient={false}>
          {[s.name, sget(s, "claim", lang), sget(s, "marquee_notice", lang)].filter(Boolean).map((txt, i) => (
            <span key={i} className="mx-8 flex items-center gap-8">
              <span className={`text-sm font-light tracking-wide ${i === 2 ? "text-[#C19D60]" : "text-[#F8F5F0]/60"}`}>{txt}</span>
              <Diamond size={8} className="fill-[#C19D60]/50 text-[#C19D60]/50" />
            </span>
          ))}
        </Marquee>
      </div>

      <section data-testid="menu-philosophy" className="mx-auto max-w-5xl px-6 py-20 md:py-28">
        <p className="mb-14 text-center text-[11px] font-medium uppercase tracking-[0.35em] text-[#C19D60]">{t("philosophy")}</p>
        <div className="grid gap-12 md:grid-cols-3 md:gap-8">
          {s.philosophy.map((p, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} custom={i * 0.12} className="text-center md:text-left">
              <span className="font-display text-5xl font-light text-transparent" style={{ WebkitTextStroke: "1px rgba(193,157,96,0.5)" }}>
                0{i + 1}
              </span>
              <h3 className="font-display mt-4 text-2xl font-medium">{phil(p, lang, "t")}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#A3A3A3]">{phil(p, lang, "d")}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="esplora" data-testid="menu-explore" className="mx-auto max-w-5xl px-5 pb-24 md:pb-32">
        <div className="mb-12 flex items-end justify-between">
          <h2 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">{t("explore")}</h2>
          <span className="hidden text-xs font-light text-[#A3A3A3] sm:block">{data.categories.length} · {data.dishes.length} {t("dishes")}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          {data.categories.filter((c) => c.available !== false).map((c, i) => (
            <motion.div key={c.id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }} custom={0.05 * (i % 3)}>
              <Link
                to={`/menu/categoria/${c.slug || c.id.replace("cat-", "")}`}
                data-testid={`menu-category-${c.slug || c.id}`}
                className="group relative block aspect-[4/5] overflow-hidden rounded-xl"
              >
                <img src={c.image} alt={loc(c.name, lang)} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10 transition-opacity duration-500 group-hover:opacity-95" />
                <span className="font-display absolute left-4 top-3 text-xl italic text-[#C19D60]/90">{String(i + 1).padStart(2, "0")}</span>
                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                  <h3 className="font-display text-xl font-medium leading-tight sm:text-2xl">{loc(c.name, lang)}</h3>
                  <span className="mt-2 block h-px w-8 bg-[#C19D60]/60 transition-[width] duration-500 group-hover:w-16" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default MenuHome;
